import { ActionConfirmation } from "../ui/ActionConfirmation.js";
import { useState, type FormEvent } from "react";
import { changeMyEmail, changeMyPassword } from "../api.js";
import { isStaff, useAuth } from "../auth.js";
import { MyDbs } from "../Dbs.js";
import { TutorAvatar } from "../TutorProfiles.js";
import {
  getMyTutorPresentation,
  type TutorPresentation,
} from "../tutorProfileApi.js";
import { useAsync } from "../useAsync.js";
import { UsageAnalyticsSettings } from "../analytics/UsageAnalytics.js";

/** Matches the server's rule, so the first thing somebody hears is not a 400. */
const MIN_PASSWORD_LENGTH = 10;

export function AccountHeader({
  identifier,
  profile,
  showTutorAvatar,
}: {
  identifier: string;
  profile: TutorPresentation | null;
  showTutorAvatar: boolean;
}) {
  const displayName = profile?.displayName ?? identifier;
  return (
    <div className="account-profile-header">
      {showTutorAvatar && (
        <TutorAvatar compact name={displayName} pictureUrl={profile?.pictureUrl ?? null} />
      )}
      <div className="account-profile-header-copy">
        <h1>Your account</h1>
        <p className="lede">Signed in as {identifier}.</p>
      </div>
    </div>
  );
}

/**
 * Your own account: the address you sign in with, and the password.
 *
 * The address is staff only, which is the server's rule and not this page's —
 * a family's address is also where their child's lesson notes go, and that
 * lives on the student record where an owner edits it. Changing it here would
 * move the sign-in and leave the notes behind, which is worse than not
 * offering it. The password carries no such second meaning and is offered to
 * anybody who has one.
 */
export function Account() {
  const { me } = useAuth();
  const staff = isStaff(me);
  const linkedTutor = staff && me?.linkedToProvider === true;
  const profile = useAsync(
    () => (linkedTutor ? getMyTutorPresentation() : Promise.resolve({ profile: null })),
    [linkedTutor, me?.user.id],
  );
  const identifier = me?.user.email ?? me?.user.username ?? "this account";

  return (
    <div className="account-page">
      <AccountHeader
        identifier={identifier}
        profile={profile.data?.profile ?? null}
        showTutorAvatar={linkedTutor}
      />

      {/* Staff only, like the address change below it — the server refuses
          anybody without a tutor profile. */}
      {me?.roles.includes("tutor") && <MyDbs />}

      {/* The server has always answered 403 to a family here, and until this
          page was reachable by anybody but staff that rule had nothing to say
          to. It does now, so the two halves are drawn apart: staff get the
          form, a family gets the sentence explaining who moves the address and
          why. A form that could only ever fail is worse than no form. */}
      {staff ? <ChangeEmail /> : <AddressIsTheSchools />}

      <ChangePassword />
      <UsageAnalyticsSettings />
    </div>
  );
}

/**
 * Why a family cannot change this here.
 *
 * Said rather than shown-and-refused, and it names the reason: the address is
 * load-bearing for something other than signing in, so moving it is not a
 * correction somebody makes about themselves.
 */
function AddressIsTheSchools() {
  return (
    <div className="panel account-card">
      <h2>The address you sign in with</h2>
      <p className="small muted">
        Ask the school to change this one. It is also where lesson notes are sent, so the two are
        moved together rather than one being left behind.
      </p>
    </div>
  );
}

function ChangeEmail() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSentTo(null);
    try {
      const result = await changeMyEmail(email, password);
      setSentTo(result.sentTo);
      setEmail("");
      setPassword("");
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Could not do that.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel account-card account-form" onSubmit={submit}>
      <h2>Change the address you sign in with</h2>
      {/* Said before they type. The reassuring half is that a typo here is
          free: the account carries on as it is until the link is used. */}
      <p className="small muted">
        We send a link to the new address, and the change happens when you use it. Until then
        nothing moves and you sign in exactly as you do now. Your old address is told, so a
        change nobody asked for cannot happen quietly.
      </p>

      {error && <div className="error small">{error}</div>}
      {sentTo && (
        <ActionConfirmation variant="success" title="Email change requested" description={sentTo} details={[{ value: "Check this address for the link. It lasts three days, and nothing changes until you use it." }]} />
      )}

      <label className="small muted" htmlFor="newEmail">
        New email
      </label>
      <input
        id="newEmail"
        type="email"
        autoComplete="email"
        autoCapitalize="none"
        spellCheck={false}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />

      <label className="small muted" htmlFor="currentPassword">
        Your current password
      </label>
      {/* Asked because a session is a weaker thing than a password — a tab
          left open on a staffroom machine is enough for one — and this
          decides where the account's password resets go. */}
      <input
        id="currentPassword"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />

      <button type="submit" disabled={busy}>
        {busy ? "Sending…" : "Send the link"}
      </button>
    </form>
  );
}

/**
 * Change the password, in place.
 *
 * Unlike the address above there is nothing to confirm and no link to wait
 * for, so this says plainly what it is about to do before it does it: the
 * change is immediate, and every other device is signed out. Somebody
 * changing a password because they think another person has it should not
 * have to guess whether that worked.
 */
function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ otherSessionsEnded: number } | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();

    // Checked here rather than at the server, because the server cannot tell
    // a mistyped confirmation from a different password and should not guess.
    if (next !== confirmation) {
      setError("Those two passwords are not the same.");
      return;
    }

    setBusy(true);
    setError(null);
    setDone(null);
    try {
      setDone(await changeMyPassword(current, next));
      setCurrent("");
      setNext("");
      setConfirmation("");
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Could not change your password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel account-card account-form" onSubmit={submit}>
      <h2>Change your password</h2>
      <p className="small muted">
        This one takes effect straight away. You stay signed in here, and every other device is
        signed out — so if you are changing it because somebody else may know it, that closes the
        door on them too.
      </p>

      {error && <div className="error small">{error}</div>}
      {done && (
        <ActionConfirmation variant="success" title="Password changed" description={<>
          Your password has been changed.{" "}
          {done.otherSessionsEnded === 0
            ? "Nothing else was signed in."
            : done.otherSessionsEnded === 1
              ? "One other device was signed out."
              : `${done.otherSessionsEnded} other devices were signed out.`}
        </>} />
      )}

      <label className="small muted" htmlFor="currentPasswordForChange">
        Your current password
      </label>
      <input
        id="currentPasswordForChange"
        type="password"
        autoComplete="current-password"
        value={current}
        onChange={(e) => setCurrent(e.target.value)}
        required
      />

      <label className="small muted" htmlFor="newPassword">
        New password
      </label>
      <input
        id="newPassword"
        type="password"
        autoComplete="new-password"
        minLength={MIN_PASSWORD_LENGTH}
        value={next}
        onChange={(e) => setNext(e.target.value)}
        required
      />
      <span className="small muted">At least {MIN_PASSWORD_LENGTH} characters.</span>

      <label className="small muted" htmlFor="newPasswordAgain">
        New password again
      </label>
      <input
        id="newPasswordAgain"
        type="password"
        autoComplete="new-password"
        minLength={MIN_PASSWORD_LENGTH}
        value={confirmation}
        onChange={(e) => setConfirmation(e.target.value)}
        required
      />

      <button type="submit" disabled={busy}>
        {busy ? "Changing…" : "Change password"}
      </button>
    </form>
  );
}
